import { useCallback, useEffect, useState } from "react";
import { fetchOperationsStatus, type OperationsStatus } from "../lib/api";
import { useInterval } from "./useInterval";

export type HealthIndicator = "online" | "degraded" | "offline";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ??
  "/api/v1";

/**
 * Polls /health/live (30s) and optionally /operations/status for degraded deps.
 */
export function useHealth(opts?: { checkDegraded?: boolean }) {
  const [indicator, setIndicator] = useState<HealthIndicator>("online");
  const [opsStatus, setOpsStatus] = useState<OperationsStatus | null>(null);

  const refreshLive = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/health/live`);
      if (!res.ok) {
        setIndicator("offline");
        return;
      }
      setIndicator("online");
    } catch {
      setIndicator("offline");
    }
  }, []);

  const refreshOps = useCallback(async () => {
    if (!opts?.checkDegraded) return;
    try {
      const status = await fetchOperationsStatus();
      setOpsStatus(status);
      const deps = Object.values(status.dependencies ?? {});
      const hasDegraded = deps.some((d) => d && !d.skipped && !d.ok);
      setIndicator((prev) => (prev === "offline" ? "offline" : hasDegraded ? "degraded" : "online"));
    } catch {
      /* keep live indicator */
    }
  }, [opts?.checkDegraded]);

  useEffect(() => {
    void refreshLive();
    void refreshOps();
  }, [refreshLive, refreshOps]);

  useInterval(refreshLive, 30_000);
  useInterval(refreshOps, 60_000);

  return { indicator, opsStatus };
}
